import React, { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import './auth-toast.scss';

type AuthNoticeType = 'login' | 'logout';

const AUTH_NOTICE_KEY = 'flashroom-auth-notice';
const AUTH_NOTICE_DURATION = 3000;

type StoredAuthNotice = {
  type: AuthNoticeType;
  createdAt: number;
};

export function queueAuthNotice(type: AuthNoticeType) {
  const notice: StoredAuthNotice = {
    type,
    createdAt: Date.now(),
  };

  sessionStorage.setItem(AUTH_NOTICE_KEY, JSON.stringify(notice));
}

function readQueuedAuthNotice(): StoredAuthNotice | null {
  const value = sessionStorage.getItem(AUTH_NOTICE_KEY);
  if (!value) return null;

  try {
    const notice = JSON.parse(value) as StoredAuthNotice;
    if (notice.type !== 'login' && notice.type !== 'logout') {
      sessionStorage.removeItem(AUTH_NOTICE_KEY);
      return null;
    }

    return notice;
  } catch {
    sessionStorage.removeItem(AUTH_NOTICE_KEY);
    return null;
  }
}

const AuthToast = () => {
  const auth = useAuth();
  const [message, setMessage] = useState('');
  const email =
    typeof auth.user?.profile.email === 'string'
      ? auth.user.profile.email
      : '';

  useEffect(() => {
    if (auth.isLoading) return;

    const notice = readQueuedAuthNotice();
    if (!notice) return;

    const isReadyToShow =
      (notice.type === 'login' && auth.isAuthenticated) ||
      (notice.type === 'logout' && !auth.isAuthenticated);

    if (!isReadyToShow) return;

    sessionStorage.removeItem(AUTH_NOTICE_KEY);
    setMessage(
      notice.type === 'login'
        ? `Successfully logged into${email ? ` ${email}` : ''}.`
        : 'You are logged out.'
    );

    const timeout = window.setTimeout(() => {
      setMessage('');
    }, AUTH_NOTICE_DURATION);

    return () => window.clearTimeout(timeout);
  }, [auth.isAuthenticated, auth.isLoading, email]);

  if (!message) return null;

  return (
    <div className="auth-toast" role="status" aria-live="polite">
      {message}
    </div>
  );
};

export default AuthToast;
